import { Component, computed } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, NavController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Subject, Subscription } from 'rxjs';
import { DialogService } from 'src/app/services/dialog/dialog.service';
import { Broadcaster } from 'src/app/services/events/broadcaster.class';
import { TenantService } from 'src/app/services/tenant/tenant.service';
import { takeUntil } from 'rxjs/operators';
import { UserService } from 'src/app/services/user/user.service';
import { CaptureHistoryService } from 'src/app/services/capture-history/capture-history.service';
import { IScanResource } from 'src/app/services/capture-history/model/IScanResource.model';
import { IPagingInfo } from 'src/app/services/capture-history/model/IPagingInfo.model';
import { ModalComponent } from '@app/components/modals/modal/modal.component';
import { CHANNELS, EventService } from 'src/app/services/events/event.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { LocaleService } from 'src/app/services/language/locale.service';
import { ISignInMethods } from '@app/services/user/models/user.model';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { PlatformService } from '@app/services/platform/platform.service';
import { ErrorService } from '@app/services/error/error.service';

@Component({
    selector: 'app-capture-history',
    templateUrl: './capture-history.page.html',
    styleUrls: ['./capture-history.page.scss'],
    standalone: false
})
export class CaptureHistoryPage {

  public appIsActive = true;
  public  isCordova: boolean = this.platformService.isCordova();
  public currentLanguage: string = null;
  public currentPage: number = 0;
  public dataIsLoaded: boolean = false;
  private logger: Logger;
  public nav: any;
  public pagingObject: IPagingInfo = null;
  private pageSize: number = 5;
  private refresher: any = null;
  public tenant = this.tenantService.tenantSignal;
  public getTenantAndUserSubscription = new Subject<void>();
  private unSubscribe = new Subject<void>();
  public unSubscribeInternetAvailability = new Subject<void>();
  public scanResources = new Array<IScanResource>();
  public  user = this.userService.userSignal;
  public isOnline = this.networkService.hasInternetAccess;

  public signInMethods: ISignInMethods = {
    LOCAL_AD: 'AD',
    AZURE: 'Microsoft',
    EMAIL: 'Email',
    GOOGLE: 'Google',
    OKTA: 'Okta',
    ONELOGIN: 'OneLogin',
    VERTICAL: 'Vertical',
    client_credentials: 'API',
    KIOSK: 'Kiosk',
    OIDC: 'OIDC'
  };

  //Pusher Event Subscriptions
  private deletedScanSubscription: Subscription = null;
  private createdScanSubscription: Subscription = null;
  private updatedScanSubscription: Subscription = null;
  public platformResumeEventSubscription: Subscription = null;
  public platformPauseEventSubscription: Subscription = null;
  public platformResumeBroadcastEventSubscription: Subscription = null;
  public languageChangeSubscription: Subscription = null;

  constructor(
    private broadcaster: Broadcaster,
    private dialogService: DialogService,
    private errorService: ErrorService,
    private eventService: EventService,
    private localeService: LocaleService,
    private loggingService: LoggingService,
    private modalCtrl: ModalController,
    private navController: NavController,
    private pageTitleService: PageTitleService,
    private platform: Platform,
    private platformService: PlatformService,
    private router: Router,
    private networkService: NetworkService,
    private tenantService: TenantService,
    public translateService: TranslateService,
    public captureHistoryService: CaptureHistoryService,
    private userService: UserService
  ) {
    this.platform.ready().then(() => {
      this.logger = this.loggingService.getLogger("[CaptureHistoryPage]");
      const methodName = "ctor";
      this.logger.entry(methodName);
    });
  }

  ngOnInit(): void {
    this.pageTitleService.setTitle('CaptureHistory');
  }

  public userSignInMethod = computed(() => {
    const user = this.user();
    if (
      user &&
      user.embedded?.authentication_details &&
      user.embedded.authentication_details.source
    ) {
      return this.signInMethods[user.embedded.authentication_details.source] || '';
    }
    return '';
  });

  public async presentInfoModal(event, target, modalType): Promise<void> {

    const dataPackage: any = {
      target: target,
      printer: target,
      cssClass: modalType
    };

    const infoModal = await this.modalCtrl.create({
      component: ModalComponent,
      componentProps: {dataPackage},
      cssClass: modalType
    });

    return await infoModal.present();
  }

  private isCurrentUrlThisPage() {
    let isThisPage = false;
    let currentUrl = null;
    const urlTree = this.router.parseUrl(this.router.url);
    urlTree.queryParams = {}
    currentUrl = urlTree.toString();
    if (currentUrl && currentUrl === '/capture-history') {
      isThisPage = true;
    }
    return isThisPage;
  }

  public refreshCaptureHistory(refresher?) {
    this.logger.info('refreshCaptureHistory()');
    this.refresher = refresher;

    if (!this.isOnline() && this.refresher) { // prevent refresher from getting stuck, when internet connection is out
      this.refresher.target.complete();
    }
    this.dataIsLoaded = false;

    this.tenantService.refreshStoredTenant()
    .pipe(takeUntil(this.getTenantAndUserSubscription))
    .subscribe(() => {
      this.userService.refreshCurrentUser()
      .pipe(takeUntil(this.getTenantAndUserSubscription))
      .subscribe(() => {
        this.getTenantAndUserSubscription.next();
        this.getTenantAndUserSubscription.complete();
        if (this.user() && this.user().embedded.user.language) {
          this.currentLanguage = this.localeService.formatLocaleString(this.user().embedded.user.language);
        }
        this.getCaptureHistory();
      });
    },
    (error) => {
      this.logger.error('handleUserAuthentication() ERROR');
      this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
      .subscribe(result => {
        if (result === 'unauthenticated') {
          this.logger.info('User unauthenticated');
        }
      });
    });
  }

  public getCaptureHistory() {
    this.logger.info('getCaptureHistory()');
    const currentUserUrl: string = this.tenant().links.currentUser;
    this.dialogService.showLoadingSpinnerDialog('getCaptureHistory()').subscribe(() => {
      this.captureHistoryService.getScanUrl(currentUserUrl)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe((scanUrl: string) => {
        this.captureHistoryService.getScanResources(scanUrl, this.currentPage, this.pageSize, 'CREATED', 'DESC')
          .pipe(takeUntil(this.unSubscribe))
          .subscribe((scanResources: Array<IScanResource>) => {
            this.dialogService.hideLoadingSpinnerDialog('getCaptureHistory()');
            this.pagingObject = scanResources["pagingInfo"];
            this.scanResources = scanResources;
            this.dataIsLoaded = true;
            if (this.refresher) {
              this.refresher.target.complete();
            }

            this.unSubscribe.next();
            this.unSubscribe.complete();
            this.handleScanStatus();
          }, (error) => {
            this.dataIsLoaded = true;
            if (this.refresher) {
              this.refresher.target.complete();
            }
            this.dialogService.hideLoadingSpinnerDialog('getCaptureHistory() - ERROR getScanResources');
            this.errorService.handleHttpClientResponseError(error, 'POST', '[CapturePage] startCaptureUpload()')
            .subscribe(result => {
              if (result === 'unauthenticated') {
                this.logger.info('User unauthenticated');
              }
            });
          });
      }, (error) => {
        this.dataIsLoaded = true;
        if (this.refresher) {
          this.refresher.target.complete();
        }
        this.dialogService.hideLoadingSpinnerDialog('getCaptureHistory() - ERROR - getScanUrl');
        this.errorService.handleHttpClientResponseError(error, 'POST', '[CapturePage] startCaptureUpload()')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
      });
    });
  }

  public handleScanStatus() {
    this.logger.info("In handleScanStatus()");
  }

  public getNextPage(showPrevious?) {
    if (showPrevious) {
      this.currentPage--;
    } else {
      this.currentPage++;
    }
    this.refreshCaptureHistory();
  }

  ionViewWillEnter(): void {
    this.pageTitleService.setTitle('CaptureHistory');
    this.platform.ready().then(() => {
      this.logger.info('ionViewWillEnter()');
      this.tenantService.refreshStoredTenant()
      .pipe(takeUntil(this.getTenantAndUserSubscription))
      .subscribe((tenant) => {
        this.userService.refreshCurrentUser()
        .pipe(takeUntil(this.getTenantAndUserSubscription))
        .subscribe(() => {
          this.getTenantAndUserSubscription.next();
          this.getTenantAndUserSubscription.complete();
          this.startListeners();
          this.refreshCaptureHistory();
        });
      }, (error) => {
        this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
      });
    });
  }

  ionViewWillLeave(): void {
    this.logger.info('ionViewWillLeave()');
    this.unSubscribe.next();
    this.unSubscribe.complete();
    this.unsubscribePusherMessages();
    this.unsubscribeEvents();
    this.unSubscribeInternetAvailability.next();
    this.unSubscribeInternetAvailability.complete();
  }

  private startListeners(): void {
    const methodName = 'startListener() ';
    this.logger.info(methodName);

    this.createdScanSubscription = this.broadcaster.on<object>(CHANNELS.SPECIFIC_USER.EVENTS.USER_SCAN_CREATED) // get request
      .subscribe(data => {
        this.logger.info('**PUSHER scan created');
        if (this.appIsActive && this.isCurrentUrlThisPage()) {
          this.refreshCaptureHistory();
        }
      });

    this.deletedScanSubscription = this.broadcaster.on<object>(CHANNELS.SPECIFIC_USER.EVENTS.USER_SCAN_DELETED)
      .subscribe(data => {
        this.logger.info('**PUSHER scan removed');
        if (this.appIsActive && this.isCurrentUrlThisPage()) {
          this.refreshCaptureHistory();
        }
      });

    this.updatedScanSubscription = this.broadcaster.on<object>(CHANNELS.SPECIFIC_USER.EVENTS.USER_SCAN_UPDATED)
      .subscribe((data: any) => {
        this.logger.info('**PUSHER scan updated');
        if (this.appIsActive && this.isCurrentUrlThisPage()) {
          this.refreshCaptureHistory();
        }
      });

    this.platformPauseEventSubscription = this.platform.pause.subscribe(async () => {
      this.appIsActive = false;
    });

    this.platformResumeEventSubscription = this.platform.resume.subscribe(async () => {
      this.appIsActive = true;
    });

    this.platformResumeBroadcastEventSubscription = this.broadcaster.on('PLATFORM_RESUMED').subscribe(() => {
      this.logger.info('PLATFORM_RESUMED');
      this.appIsActive = true;
      if (this.isCurrentUrlThisPage()) {
        this.refreshCaptureHistory();
      }
    });

    if (this.isOnline()) {
      this.refreshCaptureHistory();
    }

    this.languageChangeSubscription = this.broadcaster.on<string>('languageChange')
    .subscribe(data => {
      this.currentLanguage = this.localeService.formatLocaleString(data);
    });
  }

  private unsubscribePusherMessages(): void {
    this.logger.info('unsubscribePusherMessages()');
    this.createdScanSubscription.unsubscribe();
    this.deletedScanSubscription.unsubscribe();
    this.updatedScanSubscription.unsubscribe();
    this.languageChangeSubscription.unsubscribe();
  }

  private unsubscribeEvents() {
    this.platformResumeEventSubscription.unsubscribe();
    this.platformResumeBroadcastEventSubscription.unsubscribe();
  }
}
